import { DiscoveredCamera, OnvifCredential } from "./onvifScanInterfaces";
import { parseRtspUri } from "./parseRtspUri";

/**
 * Pick the ONVIF credential to store for a discovered camera:
 *  1. credentials embedded in the RTSP URI itself, else
 *  2. the credential at the index the scan reported authenticating with, else
 *  3. the credential whose username matches the one ONVIF authenticated with, else
 *  4. the first supplied credential.
 *
 * Index before username: the scan is given a list of credentials to try and two
 * rows may share a username with different passwords, so matching by name picks
 * the wrong one and every unmatched host silently falls back to credentials[0] --
 * which is how a whole batch of cameras ends up sharing one credential. The
 * username match is retained only for results from a gateway whose CLI predates
 * onvif_credential_index.
 *
 * `credentials` MUST be the same list, in the same order, that was sent to the
 * scan (see scanCredentials in onvifScanStore) or the index refers to nothing.
 */
export const pickCredentialForCamera = (
  camera: DiscoveredCamera,
  credentials: OnvifCredential[],
  parsedUsername: string,
  parsedPassword: string
): OnvifCredential => {
  if (parsedUsername) {
    return { username: parsedUsername, password: parsedPassword };
  }
  const index = camera.credentialIndex;
  if (index != null && index >= 0 && index < credentials.length) {
    return credentials[index];
  }
  if (camera.onvifUsernameUsed) {
    const match = credentials.find(
      (c) => c.username === camera.onvifUsernameUsed
    );
    if (match) {
      return match;
    }
  }
  return credentials[0] || { username: "", password: "" };
};

/**
 * Build the flat `values` object consumed by createSubmittedCameraObject /
 * buildSubmittedCameraObject. Field names match the manual Add Camera wizard
 * (createSubmittedCameraObjectLegacy) so an "rtsp" integration's submit_object
 * template resolves its `values.*` placeholders. `customer` and `integration`
 * come from the site's submit_object template, not from here.
 */
export const buildOnvifCameraValues = (
  camera: DiscoveredCamera,
  credentials: OnvifCredential[]
): Record<string, unknown> => {
  const parsed = parseRtspUri(camera.rtspUri, camera.ip);
  const cred = pickCredentialForCamera(
    camera,
    credentials,
    parsed.username,
    parsed.password
  );

  const vendorParts = [camera.manufacturer, camera.model].filter(
    (p) => !!p && `${p}`.trim() !== ""
  );
  const vendor = vendorParts.length > 0 ? vendorParts.join(" ") : "ONVIF Camera";
  // Channel-qualified, because one NVR host can contribute several cameras and
  // CameraOnboardingSerializer.check_valid_camera_name rejects duplicate names
  // within a site -- an unqualified name would onboard the first channel and
  // fail every one after it.
  const channelSuffix = camera.channel == null ? "" : ` ch${camera.channel}`;
  const cameraName = `${vendor} ${camera.ip}${channelSuffix}`.trim();

  // Products come from the site's onboarding selection; camera_view defaults to
  // "normal". Both are required by the RTSP camera onboarding submit_object.
  let products: unknown[] = [];
  try {
    products = JSON.parse(localStorage.getItem("selectedProducts") || "[]");
  } catch {
    products = [];
  }

  return {
    products,
    camera_view: "normal",
    camera_name: cameraName,
    ip: parsed.ip,
    rtsp: parsed.port,
    tcp: parsed.port,
    http: 80,
    // The RTSP stream path derived from ONVIF GetStreamUri.
    channel: parsed.path,
    username: cred.username,
    password: cred.password,
    camera_type: "",
    stream_parameters: "",
    // Extras carried through in case the rtsp integration template references
    // the full stream URL rather than decomposed fields.
    rtsp_url: camera.rtspUri,
    stream_url: camera.rtspUri,
  };
};
